<template>
  <form class="container">
      <areYouSureBox
        has-modal-card
        :active="showAreYouSure"
        @response="areYouSureResponseHandler"
      />
      <b-field :label="$t('GroupId')">
        <input
          type="text"
          class="input"
          :value="groupIdDisplayValue"
          data-test-id="groupIdInput"
          readonly
          disabled
        />
      </b-field>
      <b-field :label="$t('GroupName')">
        <b-input
          required
          expanded
          v-model="selectedGroup.groupName"
          :placeholder="canEditNotificationGroup ? $t('GroupNameInput') : ''"
          :validation-message="
            $t('FieldIsRequired', { fieldName: $t('GroupName') })
          "
          data-test-id="groupNameInput"
          :disabled="canEditNotificationGroup === false"
          ref="groupNameRef"
        />
      </b-field>
      <b-field :label="$t('GroupDescription')">
        <b-input
          expanded
          v-model="selectedGroup.groupDescription"
          :placeholder="
            canEditNotificationGroup ? $t('GroupDescriptionInput') : ''
          "
          :disabled="canEditNotificationGroup === false"
          data-test-id="groupDescriptionInput"
        />
      </b-field>
      <b-field grouped position="is-right">
        <b-field>
          <b-button @click="cancelUpdate">
            {{ canEditNotificationGroup ? $t("Cancel") : $t("Return") }}
          </b-button>
        </b-field>
        <b-field v-if="canEditNotificationGroup">
          <b-button
            @click="startProcessing"
            :disabled="isProcessing"
            data-test-id="groupDetailsSubmit"
          >
            {{ $t("Submit") }}
          </b-button>
        </b-field>
      </b-field>
  </form>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import * as Sentry from '@sentry/browser'
import AreYouSureBox from '@/components/AreYouSureBox.vue'
import notificationService from '@/services/notificationService'
import { useAppStore } from '@/stores/app'
import { useNotifications } from '@/composables/useNotifications'
import type { NotificationGroup } from '@/stores/app'

// Props interface
interface Props {
  id?: string | number
  canEditNotificationGroup?: boolean
  shopId?: number
}

// Emits interface
interface Emits {
  (e: 'group-update-complete'): void
  (e: 'cancel-update'): void
}

// Props with defaults
const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  canEditNotificationGroup: true,
  shopId: undefined
})

// Emits
const emit = defineEmits<Emits>()

// Composables
const { t } = useI18n()
const appStore = useAppStore()
const { openNotification } = useNotifications()

// Template refs
const groupNameRef = ref<{ checkHtml5Validity?: () => boolean } | null>(null)

// Reactive state
const selectedGroup = ref<Partial<NotificationGroup>>({})
const originalGroup = ref<Partial<NotificationGroup>>({})
const showAreYouSure = ref<boolean>(false)
const isNew = ref<boolean>(false)
const processing = ref<boolean>(false)
const groupUpdated = ref<boolean>(false)

// Computed properties
const isProcessing = computed<boolean>(() => processing.value)

const notificationGroupList = computed(() => appStore.getNotificationGroups)
const normalizedRouteGroupId = computed<number | null>(() => {
  if (props.id === undefined || props.id === null || props.id === '') {
    return null
  }

  const parsedGroupId = Number.parseInt(String(props.id), 10)
  return Number.isNaN(parsedGroupId) ? null : parsedGroupId
})
const isEditMode = computed<boolean>(() => normalizedRouteGroupId.value !== null)
const resolvedGroupId = computed<number | null>(() => {
  const currentGroupId = selectedGroup.value.groupId
  return typeof currentGroupId === 'number' && Number.isFinite(currentGroupId)
    ? currentGroupId
    : null
})
const groupIdDisplayValue = computed<string>(() => {
  if (!isEditMode.value) {
    return t('SystemGenerated')
  }
  if (resolvedGroupId.value !== null) {
    return String(resolvedGroupId.value)
  }
  return ''
})

// Methods
const areYouSureResponseHandler = async (action: string): Promise<void> => {
  if (showAreYouSure.value === true) {
    showAreYouSure.value = false
    try {
      if (action === 'yes') {
        await sendUpdate()
      }
    } finally {
      processing.value = false
    }
  }
}

const sendUpdate = async (): Promise<void> => {
  try {
    await notificationService.updateNotificationGroupFull(
      selectedGroup.value,
      isNew.value,
      props.shopId
    )

    groupUpdated.value = true
    
    openNotification({
      message: t('SuccessGroupUpdated'),
      type: 'is-success',
      'data-test-id': 'groupValidationMessage'
    })
    
    emit('group-update-complete')
  } catch (error: unknown) {
    const typedError = error as { response?: { status: number; data?: { errors?: Record<string, unknown> } } }
    
    if (typedError?.response && typedError.response.status === 409) {
      const errors = typedError.response.data?.errors ?? {}
      Object.getOwnPropertyNames(errors).forEach((err) => {
        openNotification({
          message: t(err),
          type: 'is-danger',
          'data-test-id': 'groupValidationMessage'
        })
      })
    } else {
      openNotification({
        message: t('ErrorContactUs'),
        type: 'is-danger',
        'data-test-id': 'groupValidationMessage'
      })
      Sentry.captureException(error)
    }
  }
}

const cacheOriginalGroup = (group: Partial<NotificationGroup> | null): void => {
  if (group) {
    originalGroup.value = { ...group }
  }
}

const revertToOriginalGroup = (): void => {
  Object.assign(selectedGroup.value, originalGroup.value)
}

const resetGroup = (): void => {
  selectedGroup.value = {}
  isNew.value = false
  groupUpdated.value = false
}

const cancelUpdate = (): void => {
  emit('cancel-update')
}

const startProcessing = (): void => {
  if (processing.value === false) {
    processing.value = true
    
    const validationResults = notificationService.validateGroupFields([groupNameRef.value])
    
    if (validationResults.length > 0) {
      processing.value = false
      const translatedErrors = validationResults
        .map((validator) => t(validator))
        .join(', ')
      
      openNotification({
        message: translatedErrors,
        type: 'is-danger',
        'data-test-id': 'groupValidationMessage'
      })
    } else {
      showAreYouSure.value = true
    }
  }
}

const createNewGroup = (): void => {
  if (isNew.value) {
    return
  }

  isNew.value = true
  selectedGroup.value = {
    groupId: 0,
    groupName: '',
    groupDescription: ''
  }
}

// Lifecycle hooks
onMounted(async () => {
  if (!Array.isArray(notificationGroupList.value)) {
    await notificationService.getNotificationGroups()
  }
})

onBeforeUnmount(() => {
  if (!groupUpdated.value) {
    revertToOriginalGroup()
  }
  resetGroup()
})

// Watchers
watch(
  notificationGroupList,
  (newValue) => {
    if (!isEditMode.value) {
      createNewGroup()
      return
    }

    if (!Array.isArray(newValue)) {
      return
    }

    const foundGroup = newValue.find((x) => x.groupId === normalizedRouteGroupId.value)
    selectedGroup.value = foundGroup ? { ...foundGroup } : {}
    if (foundGroup) {
      cacheOriginalGroup(selectedGroup.value)
    } else {
      originalGroup.value = {}
    }
    isNew.value = false
  },
  { immediate: true }
)
</script>
